import type { DrawerProps, ComponentEventHandler } from '@archipro-design/aria';
import { useTheme } from '@archipro-design/aria';
import { Label } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import {
    Drawer,
    Flex,
    Caption,
    Divider,
    useStyles,
} from '@archipro-design/aria';
import { useUpdateEffect } from 'ahooks';

import { useFilterStates } from './use-magazine-filter-states';
import type { FilterStates } from '../../type/filter-states';
import FilterContainer from '~/modules/search/components/filter-drawer/FilterContainer';

import { CategoryFilters } from './CategoryFilters';
import * as S from './FilterDrawer.style';
import { useState } from 'react';
import dayjs from 'dayjs';

import FilterDateDropdown from './FilterDropdown';
import type { digitalPublicationFilters } from '../../api/digital-publication-filters.server';

interface FilterDrawerProps extends Pick<DrawerProps, 'open'> {
    initialStates: FilterStates;
    onOpenChange: ComponentEventHandler<boolean>;
    onApplyFilters?: () => void;
    filters: Awaited<ReturnType<typeof digitalPublicationFilters>>;
}

const FilterDrawer = ({
    initialStates,
    open,
    onOpenChange,
    onApplyFilters,
    filters,
}: FilterDrawerProps) => {
    const {
        loading,
        filterStates,
        resetFilters,
        updateFilter,
        applyFilterChange,
        appliedFilters,
    } = useFilterStates({
        initialStates,
    });

    useUpdateEffect(() => {
        // reset filter states to initial states when open the drawer
        if (open) resetFilters(initialStates);
    }, [open]);

    const [year, setYear] = useState<number | null>(initialStates.year);
    const [month, setMonth] = useState<number | null>(initialStates.month);

    const yearDropdownValues = filters.map((f) => ({
        header: f.Year.toString(),
        key: f.Year,
    }));

    const monthItems =
        filters
            .find((f) => f.Year === year)
            ?.Months.map((m) => {
                return {
                    header: dayjs()
                        .month(m % 12)
                        .format('MMMM'),
                    key: m + 1, //Months start from 1
                    sort: m % 12,
                };
            })
            // sort ascending
            .sort((a, b) => a.sort - b.sort) ?? [];

    const { css } = useStyles({ year, month });
    const [searchQueryYear, setSearchQueryYear] = useState(year?.toString());
    const [searchQueryMonth, setSearchQueryMonth] = useState(
        monthItems.find((m) => m.key === month)?.header ?? '',
    );

    const theme = useTheme();

    return (
        <Drawer
            open={open}
            fluid={true}
            onOpenChange={(e, data) => onOpenChange(e, !!data?.open)}
            className={css(S.FilterDrawer)}
            content={
                <FilterContainer
                    secondaryButton={{
                        children: applyCount(
                            'Clear all filters',
                            appliedFilters.length,
                        ),
                        onClick: () => {
                            if (!filterStates) return;
                            resetFilters(initialStates);
                            setYear(null);
                            setMonth(null);
                            setSearchQueryYear('');
                            setSearchQueryMonth('');
                        },
                    }}
                    primaryButton={{
                        children: applyCount(
                            'View results',
                            filterStates?.totalCount ?? 0,
                            true,
                        ),
                        className: css(S.ApplyButton),
                        loading,
                        onClick: () => {
                            applyFilterChange();
                            onApplyFilters?.();
                        },
                        color: 'dark',
                    }}
                    onClose={(e) => {
                        onOpenChange(e, false);
                    }}
                    sortOptions={[]}
                    onSortChange={applyFilterChange}
                >
                    <Flex
                        design={{
                            marginTop: pxToRem(44),
                            marginBottom: pxToRem(-44),
                        }}
                    >
                        <Caption
                            variant="03"
                            weight={'medium'}
                            variables={{
                                color: theme.siteVariables.colors.charcoal[
                                    '100'
                                ],
                            }}
                        >
                            Magazines
                        </Caption>
                    </Flex>
                    <Flex column>
                        <CategoryFilters
                            onChange={(val) => {
                                updateFilter({
                                    categoryEnum: val,
                                });
                            }}
                            filterStates={filterStates}
                        />
                    </Flex>
                    <Flex column>
                        <Divider size={2} />
                        <Label
                            variables={{ fontSize: pxToRem(30) }}
                            className={css(S.PublishedLabel)}
                        >
                            Published
                        </Label>
                        <FilterDateDropdown
                            key={1}
                            items={yearDropdownValues}
                            value={year === null ? null : year.toString()}
                            defaultValue={year}
                            className={css(S.FilterYear)}
                            placeholder={'Select year'}
                            searchQuery={searchQueryYear}
                            onSearchChange={(data) => setSearchQueryYear(data)}
                            onChange={(value: number | undefined) => {
                                setYear(value ?? null);
                                if (value === undefined) {
                                    setMonth(null);
                                    setSearchQueryMonth('');
                                }
                                updateFilter({
                                    year: value,
                                    month: undefined,
                                });
                            }}
                        />
                        <FilterDateDropdown
                            key={2}
                            items={monthItems}
                            defaultValue={month}
                            value={
                                month === null
                                    ? null
                                    : monthItems[month % 12]?.header
                            }
                            className={css(S.FilterMonth)}
                            placeholder={'Select month'}
                            searchQuery={searchQueryMonth}
                            onSearchChange={(data) => setSearchQueryMonth(data)}
                            onChange={(value: number | undefined) => {
                                setMonth(value ?? null);
                                updateFilter({
                                    month: value,
                                });
                            }}
                            disabled={year === null}
                        />
                    </Flex>
                </FilterContainer>
            }
        />
    );
};

export const applyCount = (
    label: string,
    count: number,
    displayZero = false,
): string =>
    `${label} ${count || displayZero ? `(${count.toLocaleString()})` : ''}`;

export default FilterDrawer;
